import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, HelpCircle, ShoppingBag,
  RefreshCw, Play, AlertTriangle, ArrowLeftRight, Zap, Eye,
  Square, ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft,
  ArrowRightLeft,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/* ── Stat card ─────────────────────────────────────────────────────────── */
function Stat({ icon: Icon, label, value, tone = "default", testid }) {
  const palette = {
    default: { text: "text-gray-800", bg: "bg-white", bar: "bg-gray-300" },
    success: { text: "text-emerald-600", bg: "bg-emerald-50", bar: "bg-emerald-500" },
    error:   { text: "text-red-500",     bg: "bg-red-50",     bar: "bg-red-500" },
    warning: { text: "text-amber-600",   bg: "bg-amber-50",   bar: "bg-amber-500" },
    primary: { text: "text-[#0044FF]",   bg: "bg-blue-50",    bar: "bg-[#0044FF]" },
  }[tone];
  return (
    <div data-testid={testid} className={`${palette.bg} border border-[var(--border-color)] rounded-lg p-5 relative overflow-hidden`}>
      <div className={`absolute top-0 right-0 left-0 h-0.5 ${palette.bar}`} />
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${palette.bg} border border-[var(--border-color)]`}>
          <Icon className={`w-4 h-4 ${palette.text}`} />
        </div>
      </div>
      <div className={`text-3xl font-bold font-mono ${palette.text}`}>{value ?? "—"}</div>
    </div>
  );
}

/* ── Diff badge ─────────────────────────────────────────────────────────── */
function DiffBadge({ diff }) {
  if (diff === 0 || diff == null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-[var(--text-muted)] font-mono bg-[var(--bg-card-hover)] px-2 py-0.5 rounded">
        <span>=</span>
      </span>
    );
  }
  const positive = diff > 0;
  return (
    <span className={`inline-flex items-center text-xs font-mono font-bold px-2 py-0.5 rounded ${
      positive ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
    }`}>
      {positive ? "+" : ""}{diff.toLocaleString("en-US")}
    </span>
  );
}

/* ── Pagination controls ─────────────────────────────────────────────────── */
function Pagination({ page, totalPages, onPage, testPrefix = "page" }) {
  if (totalPages <= 1) return null;
  const pages = [];
  const range = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - range && i <= page + range)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "...") {
      pages.push("...");
    }
  }
  return (
    <div className="flex items-center justify-center gap-1 py-4" data-testid={`${testPrefix}-pagination`}>
      <button
        onClick={() => onPage(1)}
        disabled={page === 1}
        className="p-1.5 rounded border border-[var(--border-color)] hover:border-gray-400 disabled:opacity-30"
        data-testid={`${testPrefix}-first`}
      >
        <ChevronsRight className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => onPage(page - 1)}
        disabled={page === 1}
        className="p-1.5 rounded border border-[var(--border-color)] hover:border-gray-400 disabled:opacity-30"
        data-testid={`${testPrefix}-prev`}
      >
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} className="px-1 text-[var(--text-muted)] text-sm">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPage(p)}
            data-testid={`${testPrefix}-page-${p}`}
            className={`w-8 h-8 rounded text-xs font-medium border transition-colors ${
              p === page
                ? "bg-[#0044FF] text-white border-[#0044FF]"
                : "border-[var(--border-color)] hover:border-gray-400 text-[var(--text-secondary)]"
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onPage(page + 1)}
        disabled={page === totalPages}
        className="p-1.5 rounded border border-[var(--border-color)] hover:border-gray-400 disabled:opacity-30"
        data-testid={`${testPrefix}-next`}
      >
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => onPage(totalPages)}
        disabled={page === totalPages}
        className="p-1.5 rounded border border-[var(--border-color)] hover:border-gray-400 disabled:opacity-30"
        data-testid={`${testPrefix}-last`}
      >
        <ChevronsLeft className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════════════════════ */
export default function Preview() {
  const [report, setReport]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [syncing, setSyncing]     = useState(false);
  const [rowBusy, setRowBusy]     = useState({});

  // Pagination — matched
  const [matchedPage, setMatchedPage]         = useState(1);
  const [matchedPerPage, setMatchedPerPage]   = useState(50);

  // Pagination — unmatched
  const [unmatchedPage, setUnmatchedPage]     = useState(1);
  const [unmatchedPerPage, setUnmatchedPerPage] = useState(50);

  const setBusy   = (sku, kind) => setRowBusy((r) => ({ ...r, [sku]: kind }));
  const clearBusy = (sku)       => setRowBusy((r) => { const n = { ...r }; delete n[sku]; return n; });

  /* ── paginated slices ── */
  const matchedSlice = useMemo(() => {
    if (!report?.matched) return [];
    const start = (matchedPage - 1) * matchedPerPage;
    return report.matched.slice(start, start + matchedPerPage);
  }, [report, matchedPage, matchedPerPage]);

  const matchedTotalPages = useMemo(() =>
    report ? Math.ceil((report.matched?.length || 0) / matchedPerPage) : 0
  , [report, matchedPerPage]);

  const unmatchedSlice = useMemo(() => {
    if (!report?.unmatched_sample) return [];
    const start = (unmatchedPage - 1) * unmatchedPerPage;
    return report.unmatched_sample.slice(start, start + unmatchedPerPage);
  }, [report, unmatchedPage, unmatchedPerPage]);

  const unmatchedTotalPages = useMemo(() =>
    report ? Math.ceil((report.unmatched_sample?.length || 0) / unmatchedPerPage) : 0
  , [report, unmatchedPerPage]);

  /* ── Reset page when per-page changes ── */
  const changeMatchedPerPage = (v) => { setMatchedPerPage(v); setMatchedPage(1); };
  const changeUnmatchedPerPage = (v) => { setUnmatchedPerPage(v); setUnmatchedPage(1); };

  /* ── API calls ── */
  const syncOne = async (m) => {
    const sku = m.salla_sku || m.sku;
    setBusy(sku, "sync");
    try {
      const { data } = await api.post(`/sync/one/${encodeURIComponent(sku)}`);
      toast.success(`تم تحديث ${m.name_ar} → ${data.quantity}`);
      setReport((r) => ({
        ...r,
        matched: r.matched.map((x) =>
          (x.salla_sku || x.sku) === sku
            ? { ...x, salla_qty: data.quantity, diff: 0 }
            : x
        ),
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      clearBusy(sku);
    }
  };

  const refreshOne = async (m) => {
    const sku = m.salla_sku || m.sku;
    setBusy(sku, "refresh");
    try {
      const { data } = await api.post(`/preview/refresh/${encodeURIComponent(sku)}`);
      setReport((r) => ({
        ...r,
        matched: r.matched.map((x) =>
          (x.salla_sku || x.sku) === sku
            ? { ...x, salla_qty: data.salla_qty, diff: (x.total_stock || 0) - (data.salla_qty || 0) }
            : x
        ),
      }));
      toast.success(`كمية سلة: ${data.salla_qty ?? "—"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      clearBusy(sku);
    }
  };

  const load = async (fresh = false) => {
    setLoading(true);
    setMatchedPage(1);
    setUnmatchedPage(1);
    try {
      const { data } = await api.get("/preview/match", { params: fresh ? { fresh: true } : {} });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(true); }, []);

  const runSync = async () => {
    setSyncing(true);
    try {
      await api.post("/sync/matched", { dry_run: false });
      toast.success("بدأت المزامنة للمنتجات المطابقة");
      setTimeout(() => load(true), 2500);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const runDryRun = async () => {
    try {
      await api.post("/sync/matched", { dry_run: true });
      toast.success("تم تسجيل المعاينة في السجل");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stopSync = async () => {
    try {
      const { data } = await api.post("/sync/cancel");
      toast.success(data.cancelled_task ? "تم إيقاف المزامنة" : "لا توجد مزامنة قيد التنفيذ");
      setSyncing(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* ──────────────────────────────────────────────────────────────────────── */
  return (
    <div className="p-4 md:p-8 max-w-[1500px] mx-auto">

      {/* ── Header ── */}
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8 fade-up">
        <div>
          <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-semibold mb-1">
            معاينة قبل المزامنة
          </p>
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight">تحليل المطابقة</h1>
          <p className="text-[var(--text-muted)] mt-2 max-w-2xl text-sm leading-relaxed">
            مقارنة شاملة بين منتجات <b>قيود</b> ومنتجات <b>سلة</b> عبر SKU أو الباركود.
            النتيجة هنا <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded text-xs font-medium">بدون أي تعديل فعلي</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="preview-refresh-button"
            onClick={() => load(false)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 border border-[var(--border-color)] rounded-lg text-sm hover:border-gray-500 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            إعادة التحليل
          </button>
          <button
            data-testid="preview-fresh-button"
            onClick={() => load(true)}
            disabled={loading}
            title="تجاوز الكاش وقراءة سلة من جديد"
            className="flex items-center gap-2 px-4 py-2.5 border border-[#0044FF] text-[#0044FF] rounded-lg text-sm hover:bg-[#0044FF]/5 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            تحديث فوري
          </button>
        </div>
      </header>

      {/* ── Loading skeleton ── */}
      {!report && loading && (
        <div className="bg-white border border-[var(--border-color)] rounded-xl p-16 text-center">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-6 h-6 animate-spin text-[#0044FF]" />
          </div>
          <p className="text-sm font-medium text-[var(--text-secondary)]">جارٍ تحليل المنتجات...</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">يتم سحب منتجات سلة ومقارنتها مع قيود</p>
        </div>
      )}

      {report && (
        <>
          {/* ── KPI Cards ── */}
          <section className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Stat testid="preview-stat-qoyod"     icon={ShoppingBag}  label="منتجات قيود"  value={report.qoyod_total?.toLocaleString("en-US")}    tone="primary"  />
            <Stat testid="preview-stat-salla"     icon={ShoppingBag}  label="منتجات سلة"  value={report.salla_total?.toLocaleString("en-US")}     tone="default"  />
            <Stat testid="preview-stat-matched"   icon={CheckCircle2} label="مطابق"        value={report.matched_count?.toLocaleString("en-US")}   tone="success"  />
            <Stat testid="preview-stat-unmatched" icon={XCircle}      label="غير مطابق"   value={report.unmatched_count?.toLocaleString("en-US")} tone="error"    />
            <Stat testid="preview-stat-salla-only" icon={HelpCircle}  label="في سلة فقط"  value={report.salla_only_count?.toLocaleString("en-US")} tone="warning" />
          </section>

          {/* ── Action bar ── */}
          <section className="bg-white border border-[var(--border-color)] rounded-xl p-5 mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold mb-1">جاهز للمزامنة</div>
              <p className="text-xs text-[var(--text-muted)]">
                سيتم تحديث{" "}
                <b className="text-emerald-600 font-mono">{report.matched_count?.toLocaleString("en-US")}</b>
                {" "}منتج مطابق فقط في سلة.
                باقي الـ <span className="font-mono">{report.unmatched_count?.toLocaleString("en-US")}</span> منتج لن يُلمس.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={runDryRun}
                data-testid="preview-dryrun-button"
                className="px-4 py-2.5 text-sm border border-[var(--border-color)] rounded-lg hover:border-gray-500 transition-colors"
              >
                محاكاة المزامنة
              </button>
              {syncing && (
                <button
                  type="button"
                  onClick={stopSync}
                  data-testid="preview-stop-button"
                  className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  <Square className="w-4 h-4 fill-current" />
                  إيقاف
                </button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    disabled={!report.matched_count || syncing}
                    data-testid="preview-sync-button"
                    className="flex items-center gap-2 bg-[#0044FF] hover:bg-[#0033CC] disabled:opacity-40 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 rtl-flip" />}
                    مزامنة المطابق ({report.matched_count?.toLocaleString("en-US")})
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent dir="rtl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      تأكيد المزامنة
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      سيتم تحديث{" "}
                      <b className="font-mono">{report.matched_count?.toLocaleString("en-US")}</b>{" "}
                      منتج في سلة بالكميات الموحّدة من فروع قيود. العملية تستخدم واجهة الدفعات
                      السريعة وقد تستغرق ثوانٍ معدودة فقط.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>إلغاء</AlertDialogCancel>
                    <AlertDialogAction onClick={runSync} className="bg-[#0044FF] hover:bg-[#0033CC]">
                      نعم، زامن الآن
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </section>

          {/* ══ Matched table ══════════════════════════════════════════════════ */}
          <section className="bg-white border border-[var(--border-color)] rounded-xl mb-6 overflow-hidden">
            {/* Section header */}
            <div className="px-6 py-4 border-b border-[var(--border-color)] flex flex-wrap items-center justify-between gap-3 bg-[var(--bg-card-hover)]/50">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                منتجات مطابقة
                <span className="text-xs font-normal text-[var(--text-muted)] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full border border-emerald-200">
                  {report.matched_count?.toLocaleString("en-US")} منتج
                </span>
              </h2>
              {/* Per-page selector */}
              <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                <ArrowRightLeft className="w-3.5 h-3.5 text-[var(--text-muted)]" />
                <span>عرض كل صفحة:</span>
                {[50, 100].map((n) => (
                  <button
                    key={n}
                    data-testid={`matched-per-page-${n}`}
                    onClick={() => changeMatchedPerPage(n)}
                    className={`px-3 py-1 rounded border text-xs font-medium transition-colors ${
                      matchedPerPage === n
                        ? "bg-[#0044FF] text-white border-[#0044FF]"
                        : "border-[var(--border-color)] hover:border-gray-500 text-[var(--text-secondary)]"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                {matchedTotalPages > 1 && (
                  <span className="text-[var(--text-muted)] mr-1">
                    صفحة {matchedPage.toLocaleString("en-US")} / {matchedTotalPages.toLocaleString("en-US")}
                  </span>
                )}
              </div>
            </div>

            {report.matched.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--bg-card-hover)] flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-6 h-6 text-[var(--text-muted)]" />
                </div>
                <p className="text-sm text-[var(--text-muted)]">لا توجد منتجات مطابقة حالياً.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="matched-table">
                    <thead>
                      <tr className="border-b border-[var(--border-color)] bg-[var(--bg-card-hover)]/80">
                        <th className="text-start px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">SKU</th>
                        <th className="text-start px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">قيود ← → سلة</th>
                        <th className="text-start px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">المطابقة</th>
                        <th className="text-end px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">كمية قيود</th>
                        <th className="text-end px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">كمية سلة</th>
                        <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">الفرق</th>
                        <th className="text-center px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">إجراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matchedSlice.map((m, idx) => {
                        const sku    = m.salla_sku || m.sku;
                        const busy   = rowBusy[sku];
                        const hasDiff = m.diff !== 0 && m.diff != null;
                        return (
                          <tr
                            key={m.qoyod_id ?? `${sku}-${idx}`}
                            data-testid={`matched-row-${sku}`}
                            className={`border-b border-[var(--border-muted)] hover:bg-blue-50/30 transition-colors ${
                              hasDiff ? "bg-amber-50/20" : ""
                            }`}
                          >
                            {/* SKU */}
                            <td className="px-5 py-3.5">
                              <span className="font-mono text-xs bg-[var(--bg-card-hover)] text-[var(--text-secondary)] px-2 py-0.5 rounded">{sku || "—"}</span>
                            </td>

                            {/* Product names comparison */}
                            <td className="px-5 py-3.5 min-w-[280px]">
                              <div className="flex flex-col gap-1">
                                {/* Qoyod name */}
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[9px] font-semibold tracking-wider text-[var(--text-muted)] uppercase w-10 shrink-0">قيود</span>
                                  <span className="font-medium text-gray-800 text-sm leading-snug">{m.name_ar || "—"}</span>
                                </div>
                                {/* Salla name */}
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[9px] font-semibold tracking-wider text-[#0044FF]/70 uppercase w-10 shrink-0">سلة</span>
                                  <span className="text-[var(--text-muted)] text-xs leading-snug">{m.salla_name || "—"}</span>
                                </div>
                              </div>
                            </td>

                            {/* Match type */}
                            <td className="px-4 py-3.5">
                              <span className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-[#0044FF] px-2 py-1 rounded-full border border-blue-100 font-medium">
                                <ArrowLeftRight className="w-3 h-3" />
                                {m.match_by}
                              </span>
                            </td>

                            {/* Qoyod qty */}
                            <td className="px-4 py-3.5 text-end">
                              <span className="font-mono font-bold text-gray-800">{(m.total_stock ?? 0).toLocaleString("en-US")}</span>
                            </td>

                            {/* Salla qty */}
                            <td className="px-4 py-3.5 text-end">
                              <span className="font-mono text-[var(--text-muted)]">{m.salla_qty != null ? m.salla_qty.toLocaleString("en-US") : "—"}</span>
                            </td>

                            {/* Diff */}
                            <td className="px-4 py-3.5 text-center">
                              <DiffBadge diff={m.diff} />
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-3.5">
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  type="button"
                                  data-testid={`refresh-one-${sku}`}
                                  disabled={!!busy}
                                  onClick={() => refreshOne(m)}
                                  title="تحديث قراءة كمية سلة الآن"
                                  className="p-1.5 border border-[var(--border-color)] rounded-lg hover:border-gray-400 disabled:opacity-40 transition-colors"
                                >
                                  {busy === "refresh"
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Eye className="w-3.5 h-3.5 text-[var(--text-muted)]" />}
                                </button>
                                <button
                                  type="button"
                                  data-testid={`sync-one-${sku}`}
                                  disabled={!!busy}
                                  onClick={() => syncOne(m)}
                                  title="دفع كمية قيود إلى سلة"
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0044FF] hover:bg-[#0033CC] text-white text-xs rounded-lg disabled:opacity-50 transition-colors"
                                >
                                  {busy === "sync"
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <Zap className="w-3 h-3" />}
                                  مزامنة
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Footer: range info + pagination */}
                <div className="px-6 py-3 border-t border-[var(--border-muted)] flex flex-wrap items-center justify-between gap-2 bg-[var(--bg-card-hover)]/50">
                  <span className="text-xs text-[var(--text-muted)]">
                    {(() => {
                      const start = (matchedPage - 1) * matchedPerPage + 1;
                      const end   = Math.min(matchedPage * matchedPerPage, report.matched.length);
                      return `${start.toLocaleString("en-US")} – ${end.toLocaleString("en-US")} من ${report.matched.length.toLocaleString("en-US")} منتج`;
                    })()}
                  </span>
                  <Pagination
                    page={matchedPage}
                    totalPages={matchedTotalPages}
                    onPage={setMatchedPage}
                    testPrefix="matched"
                  />
                </div>
              </>
            )}
          </section>

          {/* ══ Unmatched table ════════════════════════════════════════════════ */}
          {(report.unmatched_sample?.length ?? 0) > 0 && (
            <section className="bg-white border border-[var(--border-color)] rounded-xl mb-6 overflow-hidden">
              <div className="px-6 py-4 border-b border-[var(--border-color)] flex flex-wrap items-center justify-between gap-3 bg-[var(--bg-card-hover)]/50">
                <div>
                  <h2 className="text-base font-semibold flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    منتجات قيود بلا مقابل في سلة
                    <span className="text-xs font-normal bg-red-50 text-red-500 px-2 py-0.5 rounded-full border border-red-200">
                      {report.unmatched_count?.toLocaleString("en-US")} منتج
                    </span>
                  </h2>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">عينة من أول 50 منتج — هذه المنتجات لن تُزامَن. أضفها في سلة بنفس الـ SKU أو الباركود لتظهر في المطابقة.</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                  <span>عرض كل صفحة:</span>
                  {[50, 100].map((n) => (
                    <button
                      key={n}
                      data-testid={`unmatched-per-page-${n}`}
                      onClick={() => changeUnmatchedPerPage(n)}
                      className={`px-3 py-1 rounded border text-xs font-medium transition-colors ${
                        unmatchedPerPage === n
                          ? "bg-[#0044FF] text-white border-[#0044FF]"
                          : "border-[var(--border-color)] hover:border-gray-500 text-[var(--text-secondary)]"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  {unmatchedTotalPages > 1 && (
                    <span className="text-[var(--text-muted)] mr-1">
                      صفحة {unmatchedPage.toLocaleString("en-US")} / {unmatchedTotalPages.toLocaleString("en-US")}
                    </span>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="unmatched-table">
                  <thead>
                    <tr className="border-b border-[var(--border-color)] bg-[var(--bg-card-hover)]/80">
                      <th className="text-start px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">SKU</th>
                      <th className="text-start px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">الباركود</th>
                      <th className="text-start px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">اسم المنتج</th>
                      <th className="text-end px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">كمية قيود</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedSlice.map((m, idx) => (
                      <tr key={m.qoyod_id ?? `unmatched-${idx}`} className="border-b border-[var(--border-muted)] hover:bg-red-50/20 transition-colors">
                        <td className="px-5 py-3">
                          <span className="font-mono text-xs bg-[var(--bg-card-hover)] text-[var(--text-muted)] px-2 py-0.5 rounded">{m.sku || "—"}</span>
                        </td>
                        <td className="px-5 py-3 font-mono text-xs text-[var(--text-muted)]">{m.barcode || "—"}</td>
                        <td className="px-5 py-3 text-[var(--text-secondary)]">{m.name_ar}</td>
                        <td className="px-5 py-3 text-end font-mono font-medium text-[var(--text-secondary)]">{(m.total_stock ?? 0).toLocaleString("en-US")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 border-t border-[var(--border-muted)] flex flex-wrap items-center justify-between gap-2 bg-[var(--bg-card-hover)]/50">
                <span className="text-xs text-[var(--text-muted)]">
                  {(() => {
                    const start = (unmatchedPage - 1) * unmatchedPerPage + 1;
                    const end   = Math.min(unmatchedPage * unmatchedPerPage, report.unmatched_sample.length);
                    return `${start.toLocaleString("en-US")} – ${end.toLocaleString("en-US")} من ${report.unmatched_count?.toLocaleString("en-US")} منتج`;
                  })()}
                </span>
                <Pagination
                  page={unmatchedPage}
                  totalPages={unmatchedTotalPages}
                  onPage={setUnmatchedPage}
                  testPrefix="unmatched"
                />
              </div>
            </section>
          )}

          {/* ══ Salla-only table ═══════════════════════════════════════════════ */}
          {(report.salla_only_sample?.length ?? 0) > 0 && (
            <section className="bg-white border border-[var(--border-color)] rounded-xl overflow-hidden mb-6">
              <div className="px-6 py-4 border-b border-[var(--border-color)] bg-[var(--bg-card-hover)]/50">
                <h2 className="text-base font-semibold flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-amber-500" />
                  منتجات في سلة فقط
                  <span className="text-xs font-normal bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full border border-amber-200">
                    {report.salla_only_count?.toLocaleString("en-US")} منتج
                  </span>
                </h2>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">هذه المنتجات موجودة في سلة لكن لا يوجد ما يطابقها في قيود. لن تُمسّ.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-color)] bg-[var(--bg-card-hover)]/80">
                      <th className="text-start px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">SKU سلة</th>
                      <th className="text-start px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">الاسم</th>
                      <th className="text-end px-5 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">الكمية</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.salla_only_sample.map((s, idx) => (
                      <tr key={s.salla_id ?? `salla-only-${idx}`} className="border-b border-[var(--border-muted)] hover:bg-amber-50/20 transition-colors">
                        <td className="px-5 py-3">
                          <span className="font-mono text-xs bg-[var(--bg-card-hover)] text-[var(--text-muted)] px-2 py-0.5 rounded">{s.sku || s.barcode || "—"}</span>
                        </td>
                        <td className="px-5 py-3 text-[var(--text-secondary)]">{s.name}</td>
                        <td className="px-5 py-3 text-end font-mono font-medium text-[var(--text-secondary)]">{s.quantity != null ? s.quantity.toLocaleString("en-US") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
